// frontend/src/pages/public/ManageRequest/ManageRequestPage.tsx 
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import {
  ApiError,
  cancelPatientRequestByToken,
  getPatientRequestByToken,
  PatientRequestStatusResponse,
} from "../../../api/familyDoctors";

import "./ManageRequestPage.css";

type ModalState =
  | null
  | {
      kind: "success" | "error" | "confirm";
      title: string;
      message: string;
      confirmLabel?: string;
      cancelLabel?: string;
      onConfirm?: () => void;
      onCancel?: () => void;
    };

function fmtDateTime(v?: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function normalizeStatusLabel(statusRaw?: string | null): string {
  const s = String(statusRaw || "").trim();
  if (!s) return "Unknown";

  const key = s.toLowerCase();

  if (key.includes("pendingemailverification") || key.includes("verify"))
    return "Email verification pending";

  if (key.includes("closedacceptedelsewhere"))
    return "Closed (attached elsewhere)";

  if (
    key.includes("reject") ||
    key.includes("declin") ||
    key.includes("notaccepted")
  )
    return "Not accepted";

  if (key.includes("submitted")) return "Submitted";
  if (
    key.includes("inreview") ||
    key.includes("in_review") ||
    key.includes("review")
  )
    return "Under review";
  if (key.includes("wait")) return "Waitlisted";
  if (key.includes("accept")) return "Invited for Meet & Greet";
  if (key.includes("cancel")) return "Cancelled";
  if (key.includes("expire")) return "Expired";

  return s;
}

function getStatusPillStyle(label: string): React.CSSProperties {
  const k = label.toLowerCase();

  if (k.includes("invited for meet") || k.includes("meet & greet")) {
    return {
      background: "rgba(34, 197, 94, 0.12)",
      borderColor: "rgba(34, 197, 94, 0.2)",
      color: "#16a34a",
    };
  }
  if (k.includes("not accepted") || k.includes("rejected") || k.includes("declined")) {
    return {
      background: "rgba(239, 68, 68, 0.1)",
      borderColor: "rgba(239, 68, 68, 0.2)",
      color: "#dc2626",
    };
  }
  if (k.includes("attached elsewhere")) {
    return {
      background: "rgba(107, 122, 148, 0.1)",
      borderColor: "rgba(107, 122, 148, 0.2)",
      color: "#6b7a94",
    };
  }
  if (k.includes("cancel")) {
    return { background: "#fff3e6", borderColor: "#ffd8b3", color: "#7a3b00" };
  }
  if (k.includes("expired")) {
    return {
      background: "rgba(107, 122, 148, 0.1)",
      borderColor: "rgba(107, 122, 148, 0.2)",
      color: "#6b7a94",
    };
  }

  // Default: submitted / under review / waitlisted / email confirmation pending
  return {
    background: "rgba(22, 105, 179, 0.1)",
    borderColor: "rgba(22, 105, 179, 0.2)",
    color: "#0f5087",
  };
}

function canCancel(statusLabel: string): boolean {
  const k = statusLabel.toLowerCase();

  if (k.includes("invited for meet") || k.includes("meet & greet")) return false;
  if (k.includes("not accepted")) return false;
  if (k.includes("cancel")) return false;
  if (k.includes("expired")) return false;
  if (k.includes("attached elsewhere")) return false;

  return true;
}

function safeTrim(v: any): string {
  return String(v ?? "").trim();
}

function makeInitials(name: string): string {
  const s = safeTrim(name);
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
}

export default function ManageRequestPage() {
  const { token } = useParams<{ token: string }>();
  const safeToken = useMemo(() => String(token || "").trim(), [token]);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [data, setData] = useState<PatientRequestStatusResponse | null>(null);

  const [cancelling, setCancelling] = useState<boolean>(false);
  const [modal, setModal] = useState<ModalState>(null);

  const statusLabel = useMemo(
    () => normalizeStatusLabel(data?.status),
    [data?.status]
  );

  const pillStyle = useMemo(
    () => getStatusPillStyle(statusLabel),
    [statusLabel]
  );

  const allowCancel = useMemo(() => canCancel(statusLabel), [statusLabel]);

  const verifiedAt = (data as any)?.timeline?.verifiedAt as
    | string
    | null
    | undefined;
  const isVerified = !!verifiedAt;

  // Patient label (best-effort, non-breaking if API doesn't include it yet)
  const patientName = useMemo(() => {
    const d: any = data as any;
    const direct =
      safeTrim(d?.patient?.name) ||
      safeTrim(d?.patientName) ||
      safeTrim(d?.patient?.fullName) ||
      safeTrim(d?.patient?.full_name) ||
      safeTrim(d?.patient?.FullName);

    if (direct) return direct;

    const fn =
      safeTrim(d?.patient?.firstName) ||
      safeTrim(d?.patientFirstName) ||
      safeTrim(d?.patient?.FirstName);

    const ln =
      safeTrim(d?.patient?.lastName) ||
      safeTrim(d?.patientLastName) ||
      safeTrim(d?.patient?.LastName);

    const combined = `${fn} ${ln}`.trim();
    return combined || "";
  }, [data]);

  const patientInitials = useMemo(() => makeInitials(patientName), [patientName]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!safeToken) {
        setLoading(false);
        setLoadError("Missing token.");
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setLoadError(null);

        const resp = await getPatientRequestByToken(safeToken);
        if (cancelled) return;

        setData(resp);
      } catch (e: any) {
        if (cancelled) return;

        let msg = "Could not load this request.";
        if (e instanceof ApiError) {
          if (e.status === 404 || e.status === 410)
            msg = "This link is invalid or has expired.";
          else msg = e.message || msg;
        }
        setLoadError(msg);
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [safeToken]);

  async function handleCancel() {
    if (!safeToken) return;
    if (cancelling) return;

    try {
      setCancelling(true);

      const resp = await cancelPatientRequestByToken(safeToken);

      if (resp?.success) {
        // NOTE:
        // By design, cancelling may immediately invalidate this manage token (or make
        // the status endpoint return 410 for finalized requests). So we must NOT
        // re-fetch status here, otherwise we can incorrectly show an error after a
        // successful cancellation.
        setModal({
          kind: "success",
          title: "Request cancelled",
          message:
            "Your request has been cancelled successfully. If you change your mind, you can submit a new request from the doctor's profile page.",
        });

        // Optimistically update local UI state so the Cancel button disappears
        // (do not rely on re-fetching with the same token).
        setData((prev) =>
          prev
            ? ({
                ...prev,
                status: "Cancelled",
                timeline: {
                  ...(prev as any).timeline,
                  cancelledAt: new Date().toISOString(),
                },
              } as any)
            : prev
        );
      } else {
        setModal({
          kind: "error",
          title: "Could not cancel",
          message:
            "We couldn't cancel this request. It may already be finalized or no longer eligible for cancellation.",
        });
      }
    } catch (e: any) {
      let msg =
        "We couldn't cancel this request. It may already be finalized or no longer eligible for cancellation.";
      if (e instanceof ApiError) {
        if (e.status === 404 || e.status === 410)
          msg = "This link is invalid or has expired.";
        else msg = e.message || msg;
      }
      setModal({ kind: "error", title: "Could not cancel", message: msg });
    } finally {
      setCancelling(false);
    }
  }

  function openCancelConfirm() {
    setModal({
      kind: "confirm",
      title: "Cancel request?",
      message:
        "If you cancel, the clinic will stop reviewing this request. You can submit a new request later from the doctor's profile page.",
      confirmLabel: "Cancel request",
      cancelLabel: "Keep request",
      onConfirm: async () => {
        setModal(null);
        await handleCancel();
      },
      onCancel: () => setModal(null),
    });
  }

  const doctorName = data?.doctor?.name || "—";
  const clinicName = data?.clinic?.name || "";
  const expiresAt = data?.token?.expiresAt || null;

  const getModalIcon = (kind: string) => {
    if (kind === "success") {
      return (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="24" fill="#16a34a" fillOpacity="0.12" />
          <path
            d="M16 24L22 30L32 18"
            stroke="#16a34a"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (kind === "error") {
      return (
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="24" fill="#dc2626" fillOpacity="0.12" />
          <circle cx="24" cy="24" r="12" stroke="#dc2626" strokeWidth="2" />
          <path
            d="M24 18V26"
            stroke="#dc2626"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <circle cx="24" cy="30" r="1.5" fill="#dc2626" />
        </svg>
      );
    }
    // confirm
    return (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="24" fill="#f59e0b" fillOpacity="0.12" />
        <path
          d="M24 16V28"
          stroke="#f59e0b"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="24" cy="33" r="2" fill="#f59e0b" />
      </svg>
    );
  };

  return (
    <div className="mr-page">
      <PublicHeader variant="solid" />

      {/* Modal */}
      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          className="mr-modal-overlay"
          onClick={() => setModal(null)}
        >
          <div
            className={`mr-modal mr-modal--${modal.kind}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mr-modal-close"
              onClick={() => setModal(null)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="mr-modal-icon">{getModalIcon(modal.kind)}</div>

            <div className="mr-modal-title">{modal.title}</div>
            <div className="mr-modal-message">{modal.message}</div>

            <div className="mr-modal-actions">
              {modal.kind === "confirm" ? (
                <>
                  <button
                    type="button"
                    className="mr-btn"
                    onClick={() => modal.onCancel?.()}
                    disabled={cancelling}
                  >
                    {modal.cancelLabel || "Keep"}
                  </button>
                  <button
                    type="button"
                    className="mr-btn mr-btn-danger"
                    onClick={() => modal.onConfirm?.()}
                    disabled={cancelling}
                  >
                    {cancelling ? "Cancelling…" : modal.confirmLabel || "Confirm"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="mr-btn mr-btn-primary"
                  onClick={() => setModal(null)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="mr-container">
        {/* Top navigation */}
        <div className="mr-topnav">
          <Link to="/public/find-family-doctor" className="mr-back-link">
            ← Back to search
          </Link>
        </div>

        {/* Header card */}
        <div className="mr-card">
          <h1 className="mr-title">Manage your request</h1>

          <div className="mr-header">
            <div className="mr-doctor-line">
              Doctor: <span className="mr-doctor-name">{doctorName}</span>
            </div>
            {clinicName && <div className="mr-clinic-name">{clinicName}</div>}

            {/* ✅ NEW: Patient identifier (name + initials) */}
            {patientName ? (
              <div
                className="mr-doctor-line"
                style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}
              >
                {patientInitials ? (
                  <span
                    aria-hidden="true"
                    title={patientName}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      background: "rgba(22, 105, 179, 0.12)",
                      border: "1px solid rgba(22, 105, 179, 0.2)",
                      color: "#0f5087",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: 0.5,
                      flex: "0 0 auto",
                    }}
                  >
                    {patientInitials}
                  </span>
                ) : null}

                <div>
                  <div>
                    Patient: <span className="mr-doctor-name">{patientName}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mr-info">
            Use this secure page to check the status of your request. If cancellation is still available, you can cancel your request here.
          </div>
        </div>

        {/* Content card */}
        {loading ? (
          <div className="mr-card">
            <div className="mr-loading">Loading…</div>
            <div className="mr-loading-desc">Please wait while we retrieve your request.</div>
          </div>
        ) : loadError ? (
          <div className="mr-card">
            <div className="mr-error">
              <b>Could not load request.</b> {loadError}
            </div>
            <div className="mr-actions">
              <Link to="/public/find-family-doctor" className="mr-btn mr-btn-primary">
                Find a family doctor
              </Link>
            </div>
          </div>
        ) : data ? (
          <div className="mr-card">
            {/* Verification warning banner */}
            {!isVerified && (
              <div className="mr-warning-banner">
                Email verification pending. Please check your email and use the verification link. Your request will not be sent to the clinic until you verify your email.
              </div>
            )}

            {/* Status pill */}
            <div
              className="mr-status-pill"
              style={{
                background: pillStyle.background,
                border: `1px solid ${pillStyle.borderColor}`,
                color: pillStyle.color,
              }}
            >
              Status: {statusLabel}
            </div>

            {/* Status description */}
            <div className="mr-status-desc">
              {statusLabel.toLowerCase().includes("invited for meet") ||
              statusLabel.toLowerCase().includes("meet & greet") ? (
                <span>
                  The clinic has invited you for a Meet & Greet. This invitation does not by itself confirm attachment to the practice. The clinic will contact you with next steps. Attachment is completed only after the clinic confirms the process is complete.
                </span>
              ) : statusLabel.toLowerCase().includes("attached elsewhere") ? (
                <span>
                  This request was closed because you became attached to another family doctor. You don't need to do anything.
                </span>
              ) : statusLabel.toLowerCase().includes("not accepted") ? (
                <span>The clinic was unable to accept your request. You can continue searching for another participating family doctor.</span>
              ) : statusLabel.toLowerCase().includes("cancel") ? (
                <span>Your request has been cancelled.</span>
              ) : statusLabel.toLowerCase().includes("expired") ? (
                <span>This request has expired because the clinic did not take action within 7 days. The request is no longer active.</span>
              ) : !isVerified ? (
                <span>Your request will be sent to the clinic after you confirm your email.</span>
              ) : statusLabel.toLowerCase().includes("wait") ? (
                <span>Your request has been placed on the clinic's waitlist. The clinic will contact you if there is an update or next step.</span>
              ) : (
                <span>
                  Your request is being reviewed. If the clinic needs more details, they may contact you.
                </span>
              )}
            </div>

            {/* Timeline */}
            <div className="mr-timeline">
              <div className="mr-timeline-title">Timeline</div>
              <div className="mr-timeline-list">
                <div>
                  <span className="mr-timeline-label">Created:</span>{" "}
                  {fmtDateTime((data as any)?.timeline?.createdAt)}
                </div>
                <div>
                  <span className="mr-timeline-label">Submitted:</span>{" "}
                  {fmtDateTime((data as any)?.timeline?.submittedAt)}
                </div>
                <div>
                  <span className="mr-timeline-label">Email verified:</span>{" "}
                  {fmtDateTime((data as any)?.timeline?.verifiedAt)}
                </div>
                <div>
                  <span className="mr-timeline-label">Decision:</span>{" "}
                  {fmtDateTime((data as any)?.timeline?.decisionAt)}
                </div>
                <div>
                  <span className="mr-timeline-label">
                    {statusLabel.toLowerCase().includes("attached elsewhere") ? "Closed:" : "Cancelled:"}
                  </span>{" "}
                  {fmtDateTime((data as any)?.timeline?.cancelledAt)}
                </div>
                <div>
                  <span className="mr-timeline-label">Expired:</span>{" "}
                  {fmtDateTime((data as any)?.timeline?.autoExpiredAt)}
                </div>
              </div>
            </div>

            {/* Token expiry */}
            <div className="mr-expiry">
              Secure manage link expiry: <b>{fmtDateTime(expiresAt)}</b>
            </div>

            {/* Actions */}
            <div className="mr-actions">
              {allowCancel && (
                <button
                  type="button"
                  className="mr-btn mr-btn-danger"
                  disabled={cancelling}
                  onClick={openCancelConfirm}
                >
                  {cancelling ? "Cancelling…" : "Cancel request"}
                </button>
              )}
              <Link to="/public/find-family-doctor" className="mr-btn">
                Find another doctor
              </Link>
            </div>

            <div className="mr-help">Tip: Save this link if you want to check the status later.</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}